import TemplateService from "@/services/template";
import { TEMPLATE_ITEM_TYPE, TEMPLATE_TYPES } from "@/consts/template";
import { i18n } from "@/boot/i18n";
import { v4 as uuidv4 } from "uuid";
import translations from "@/i18n";
import * as types from "./mutationTypes";
import ErrorHandler from "@/tools/errorHandler";
import { MIN_LENGTH, REQUIRED } from "../validator/methods";
import { getArrayPositionsWordInText } from "@/utils/string";

export default {
  initListState({ commit }) {
    commit(types.SET_TEMPLATES, []);
    commit(types.SET_TOTAL_TEMPLATES, 0);
  },
  clearListState({ commit }) {
    commit(types.CLEAR_TEMPLATES);
    commit(types.CLEAR_TOTAL_TEMPLATES);
  },
  initNewState({ dispatch }) {
    const validator = validators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
    dispatch("validator/addValidator", validator.typeValidator, { root: true });
    dispatch("validator/addValidator", validator.languagesValidator, { root: true });
  },
  clearNewState({ commit, dispatch }) {
    commit(types.CLEAR_SELECTED_ITEM_INDEX);
    commit(types.CLEAR_CURRENT_LANGUAGE);
    commit(types.CLEAR_TEMPLATE);
    dispatch("validator/clearValidator", null, { root: true });
  },
  setTemplatesSuccess({ commit, rootState }, { templates, total }) {
    templates.map((template) => {
      if (template.root) {
        replaceOrgImage(template.items, rootState.organization.organization);
        replaceInviteText(template.items, rootState.organization.organization);
      }
      return template;
    });
    commit(types.SET_TEMPLATES, templates);
    commit(types.SET_TOTAL_TEMPLATES, total);
  },
  setTemplateSuccess({ rootState, commit, state, dispatch }, template) {
    dispatch("validator/clearValidator", null, { root: true });
    const validator = validators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
    dispatch("validator/addValidator", validator.typeValidator, { root: true });
    dispatch("validator/addValidator", validator.languagesValidator, { root: true });
    const { logo } = rootState.organization.organization;
    template.items = template.items.map((i) => {
      const validator = getItemValidator(i, state.template.type);
      dispatch("validator/addValidator", validator, { root: true });
      if (i.item_type === TEMPLATE_ITEM_TYPE.IMAGE) {
        return { ...i, image_src: i.image_src.replace("{{org.image}}", logo) };
      } else {
        return { ...i };
      }
    });
    if (!state.selectedLanguage) {
      commit(types.SET_CURRENT_LANGUAGE, template.languages[0]);
    }
    commit(types.SET_TEMPLATE, template);
  },
  async getAll({ rootState, dispatch }, pagination) {
    try {
      const response = await TemplateService.getAll(rootState.organization.organization.uuid, pagination);
      if (response.data.data.templates) {
        dispatch("setTemplatesSuccess", {
          templates: response.data.data.templates,
          total: response.data.data.total_rows
        });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async delete({ rootState, dispatch }, templateUUID) {
    try {
      await TemplateService.delete(rootState.organization.organization.uuid, templateUUID);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOne({ rootState, dispatch }, templateUUID) {
    try {
      const { uuid } = rootState.organization.organization;
      const response = await TemplateService.getOne(uuid, templateUUID);
      dispatch("setTemplateSuccess", response.data.data);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async save({ rootState, state, dispatch }, { onSuccess = () => {}, onError = () => {} }) {
    const valid = await dispatch("validate");
    if (!valid) {
      onError();
      return;
    }

    const organizationUUID = rootState.organization.organization.uuid;
    const template = { ...state.template };
    template.items.forEach((item) => {
      if (item.item_type === TEMPLATE_ITEM_TYPE.TEXT) {
        item.text = changeLinkInText(item);
      }
    });

    try {
      let response;
      if (state.template.uuid) {
        response = await TemplateService.update(organizationUUID, state.template);
      } else {
        template.items.forEach((i) => {
          delete i.uuid;
        });
        response = await TemplateService.create(organizationUUID, state.template);
      }
      dispatch("setTemplateSuccess", response.data.data);
      dispatch("ui/showToastSuccess", i18n.t("template.messages.save_success"), { root: true });
      onSuccess();
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  setTemplate({ commit }, template) {
    commit(types.SET_TEMPLATE, template);
  },
  setName({ commit, dispatch }, value) {
    commit(types.SET_NAME, value);
    dispatch("validator/checkValidation", { field: "name", value: value }, { root: true });
  },
  setType({ commit, dispatch, state, rootState }, value) {
    commit(types.SET_TYPE, value);
    dispatch("clearItems");
    const organizationLanguage = rootState.organization.organization.primary_language;
    commit(types.SET_CURRENT_LANGUAGE, organizationLanguage);
    commit(types.SET_LANGUAGE, [organizationLanguage]);
    const template = getDefaultTemplateByType(value, state.selectedLanguage);
    dispatch("setTemplateItems", template.items);
    dispatch("setSelectedItem", null);
    dispatch("validator/checkValidation", { field: "type", value: value }, { root: true });
  },
  setLanguage({ commit, dispatch, state }, selectedLanguages) {
    let languages = selectedLanguages;

    const removeList = state.template.items.filter((i) => !selectedLanguages.includes(i.language));
    removeList.forEach((i) => dispatch("removeItem", i.uuid));

    selectedLanguages.forEach((language) => {
      const hasItemsLanguage = state.template.items.find((item) => item.language === language);
      if (!hasItemsLanguage) {
        const template = getDefaultTemplateByType(state.template.type, language);
        dispatch("setTemplateItems", template.items);
      }
    });

    const uniqueLanguages = [...new Set(languages)];
    commit(types.SET_LANGUAGE, uniqueLanguages);
    commit(types.SET_CURRENT_LANGUAGE, uniqueLanguages[0]);
    dispatch("validator/checkValidation", { field: "languages", value: selectedLanguages }, { root: true });
  },
  setDefault({ commit }, value) {
    commit(types.SET_DEFAULT, value);
  },
  setCurrentLanguage({ commit, dispatch }, value) {
    commit(types.SET_CURRENT_LANGUAGE, value);
    dispatch("setSelectedItem", null);
  },
  addItemByType({ state, dispatch }, itemType) {
    const item = getDefaultTemplateItem(itemType, false, null, state.selectedLanguage);
    dispatch("addItem", item);
    dispatch("setSelectedItem", item.uuid);
  },
  addItem({ commit, dispatch, state }, item) {
    commit(types.ADD_ITEM, item);
    const validator = getItemValidator(item, state.template.type);
    dispatch("validator/addValidator", validator, { root: true });
  },
  removeItem({ state, commit, dispatch }, uuid) {
    const index = state.template.items.findIndex((i) => i.uuid === uuid);
    commit(types.REMOVE_ITEM, index);
    dispatch("validator/removeError", "item:" + uuid, { root: true });
  },
  setTemplateItems({ dispatch }, templateItems) {
    templateItems.map((item) => dispatch("addItem", item));
  },
  clearItems({ commit }) {
    commit(types.CLEAR_ITEMS);
  },
  clearItemsByLanguage({ commit }, language) {
    commit(types.CLEAR_ITEMS_BY_LANGUAGE, language);
  },
  clearTemplates({ commit }) {
    commit(types.CLEAR_TEMPLATES);
    commit(types.CLEAR_TOTAL_TEMPLATES);
  },
  setSelectedItem({ state, commit }, uuid) {
    if (!uuid) {
      commit(types.SET_SELECTED_ITEM_INDEX, null);
      return;
    }
    const index = state.template.items.findIndex((i) => i.uuid === uuid);
    commit(types.SET_SELECTED_ITEM_INDEX, index);
  },
  updateItem({ state, commit, dispatch }, { uuid, field, value }) {
    const itemIndex = state.template.items.findIndex((i) => i.uuid === uuid);
    commit(types.UPDATE_ITEM, { itemIndex, field, value });
    const item = state.template.items[itemIndex];
    dispatch("validator/checkValidation", { field: "item:" + uuid, value: item }, { root: true });
  },
  duplicateItem({ state, commit }, uuid) {
    const itemIndex = state.template.items.findIndex((i) => i.uuid === uuid);
    commit(types.DUPLICATE_ITEM, itemIndex);
  },
  duplicate({ commit }, template) {
    const newTemplate = { ...template };
    newTemplate.uuid = null;
    newTemplate.name = template.name + " copy";
    newTemplate.items = [];
    newTemplate.root = false;
    newTemplate.is_default = false;
    template.items.forEach((item) => {
      const itemDuplicated = { ...item };
      itemDuplicated.uuid = uuidv4();
      newTemplate.items.push(itemDuplicated);
    });
    commit(types.SET_TEMPLATE, newTemplate);
  },
  validate({ state, rootState, dispatch }) {
    if (state.template.root) {
      return true;
    }
    dispatch("validator/validate", { field: "name", value: state.template.name }, { root: true });
    dispatch("validator/validate", { field: "type", value: state.template.type }, { root: true });
    dispatch("validator/validate", { field: "languages", value: state.template.languages }, { root: true });

    state.template.items.forEach((i) => {
      dispatch("validator/validate", { field: "item:" + i.uuid, value: i }, { root: true });
    });

    return rootState.validator.errors.length === 0;
  }
};

const validators = () => {
  return {
    nameValidator: {
      field: "name",
      validations: [{ method: REQUIRED, message: i18n.t("template.errors.no_name") }],
      meta: { tabIndex: 0 }
    },
    typeValidator: {
      field: "type",
      validations: [{ method: REQUIRED, message: i18n.t("template.errors.no_type") }],
      meta: { tabIndex: 0 }
    },
    languagesValidator: {
      field: "languages",
      validations: [{ method: MIN_LENGTH, value: 1, message: i18n.t("template.errors.no_languages") }],
      meta: { tabIndex: 0 }
    }
  };
};

function changeTargetInTextBySettings(item) {
  const targetSelf = 'target="_self"',
    targetBlank = 'target="_blank"';

  return item.open_link_new_tab
    ? { text: item.text.replaceAll(targetSelf, targetBlank), target: targetBlank }
    : { text: item.text.replaceAll(targetBlank, targetSelf), target: targetSelf };
}

function getTextElementsPositions(item, targetTextObject) {
  const searchHrefStr = "href",
    searchRelStr = "rel";

  return {
    hrefs: getArrayPositionsWordInText(item.text, searchHrefStr),
    rels: getArrayPositionsWordInText(item.text, searchRelStr),
    targets: getArrayPositionsWordInText(item.text, targetTextObject.target),
    finalArchor: getArrayPositionsWordInText(item.text, "</a>")
  };
}

function changeLinkInText(item) {
  const searchHrefStr = "href",
    http = "http";

  const targetTextObject = changeTargetInTextBySettings(item);
  item.text = targetTextObject.text;

  const indexesHref = getArrayPositionsWordInText(item.text, searchHrefStr);
  if (indexesHref.length > 0) {
    for (var i = 0; i <= indexesHref.length; i++) {
      const elements = getTextElementsPositions(item, targetTextObject);
      const initialHref = elements.hrefs[i] + searchHrefStr.length + 1;
      const finalHref = elements.rels[i];
      const link = item.text
        .substring(elements.hrefs[i], elements.rels[i])
        .trim()
        .replace("href=", "")
        .replaceAll('"', "");

      if (link.length > 0 && link.indexOf(http) < 0) {
        const initialText = item.text.substring(0, initialHref);
        const linkText = '"' + http + "://" + link.trim().replaceAll('"', "") + '" ';
        const finalText = item.text.substring(finalHref);
        item.text = initialText + linkText + finalText;
      }
    }
  }

  return item.text;
}

function getItemValidator(item, templateType) {
  switch (item.item_type) {
    case TEMPLATE_ITEM_TYPE.SENDER:
      return {
        field: "item:" + item.uuid,
        validations: [
          { method: validateSenderItem, message: i18n.t("template.errors.no_sender") + ` [${item.language}]` }
        ],
        meta: { tabIndex: 1, uuid: item.uuid }
      };
    case TEMPLATE_ITEM_TYPE.IMAGE:
      return {
        field: "item:" + item.uuid,
        validations: [
          { method: (i) => !!i.image_src, message: i18n.t("template.errors.no_image") + ` [${item.language}]` }
        ],
        meta: { tabIndex: 1, uuid: item.uuid }
      };
    case TEMPLATE_ITEM_TYPE.START_SURVEY:
      return {
        field: "item:" + item.uuid,
        validations: [
          {
            method: (i) => !i.start_button || (i.start_button && i.text_button),
            message: i18n.t("template.errors.no_text_button") + ` [${item.language}]`
          }
        ],
        meta: { tabIndex: 1, uuid: item.uuid }
      };

    default:
      if (
        templateType === TEMPLATE_TYPES.SURVEY_REMINDER_LINKSMS ||
        templateType === TEMPLATE_TYPES.SURVEY_INVITE_LINKSMS
      ) {
        return {
          field: "item:" + item.uuid,
          validations: [
            {
              method: (i) => i.text.length < 135,
              message: i18n.t("template.errors.text_length_sms") + ` [${item.language}]`
            }
          ],
          meta: { tabIndex: 1, uuid: item.uuid }
        };
      } else {
        return {
          field: "item:" + item.uuid,
          validations: [
            {
              method: (i) => {
                return !!i.text;
              },
              message: i18n.t("template.errors.no_text") + ` [${item.language}]`
            }
          ],
          meta: { tabIndex: 1, uuid: item.uuid }
        };
      }
  }
}

function validateSenderItem(item) {
  if (!item.email || !item.from || !item.subject) {
    return false;
  }
  return true;
}

const getDefaultTemplateItem = function (type, required = false, customText = "", language) {
  let text = "";
  if (customText) {
    text = customText;
  } else {
    text = i18n.t("general.insert_text");
  }
  const languagePack = translations[language];
  switch (type) {
    case TEMPLATE_ITEM_TYPE.SENDER:
      return {
        uuid: uuidv4(),
        item_type: type,
        email: "",
        from: languagePack.template?.items?.sender?.from || "",
        subject: languagePack.template?.items?.sender?.subject || "",
        fixed: true,
        required,
        language
      };
    case TEMPLATE_ITEM_TYPE.IMAGE:
      return {
        uuid: uuidv4(),
        item_type: type,
        image_src: "",
        image_align: "center",
        required,
        language
      };
    case TEMPLATE_ITEM_TYPE.START_SURVEY:
      return {
        uuid: uuidv4(),
        item_type: type,
        start_button: true,
        text_button: languagePack.template?.items?.start_survey?.answer_now || "",
        button_color: "#54CA7E",
        scale_subtitle: "",
        language
      };
    case TEMPLATE_ITEM_TYPE.SIGNATURE:
      return {
        uuid: uuidv4(),
        item_type: type,
        text: languagePack.template?.items?.sign?.default_text || "",
        required,
        fixed: true,
        language
      };
    default:
      return {
        uuid: uuidv4(),
        item_type: type,
        language,
        text,
        required,
        open_link_new_tab: true
      };
  }
};

const getDefaultTemplateByType = function (type, language) {
  const languagePack = translations[language];
  switch (type) {
    case TEMPLATE_TYPES.SURVEY_COMPLETED:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.IMAGE, false, null, language),
          getDefaultTemplateItem(
            TEMPLATE_ITEM_TYPE.TEXT,
            false,
            languagePack.template?.types?.survey_completed?.default_text_item || "",
            language
          )
        ]
      };
    case TEMPLATE_TYPES.SURVEY_EXPIRED:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.IMAGE, false, null, language),
          getDefaultTemplateItem(
            TEMPLATE_ITEM_TYPE.TEXT,
            false,
            languagePack.template?.types?.survey_expired?.default_text_item || "",
            language
          )
        ]
      };
    case TEMPLATE_TYPES.SURVEY_INVITE:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.SENDER, true, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.IMAGE, false, null, language),
          getDefaultTemplateItem(
            TEMPLATE_ITEM_TYPE.TEXT,
            false,
            languagePack.template?.types?.survey_invite?.default_text_item || "",
            language
          ),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.START_SURVEY, true, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.SIGNATURE, false, null, language)
        ]
      };
    case TEMPLATE_TYPES.SURVEY_REMINDER:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.SENDER, true, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.IMAGE, false, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.TEXT, false, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.START_SURVEY, true, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.SIGNATURE, false, null, language)
        ]
      };
    case TEMPLATE_TYPES.FINISH_LOOP:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.IMAGE, false, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.TEXT, false, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.SIGNATURE, false, null, language)
        ]
      };
    case TEMPLATE_TYPES.CLOSSING_LOOP:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.IMAGE, false, null, language),
          getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.TEXT, false, null, language)
        ]
      };
    case TEMPLATE_TYPES.SURVEY_INVITE_LINKSMS:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(
            TEMPLATE_ITEM_TYPE.TEXT,
            true,
            languagePack.template?.types?.sms_invite?.default_text_item || "",
            language
          )
        ]
      };
    case TEMPLATE_TYPES.SURVEY_REMINDER_LINKSMS:
      return {
        template_type: type,
        items: [
          getDefaultTemplateItem(
            TEMPLATE_ITEM_TYPE.TEXT,
            true,
            languagePack.template?.types?.sms_reminder?.default_text_item || "",
            language
          )
        ]
      };
    default:
      return {
        template_type: type,
        items: [getDefaultTemplateItem(TEMPLATE_ITEM_TYPE.TEXT, false, null, language)]
      };
  }
};

function replaceOrgImage(items, organization) {
  for (let i = 0; i < items.length; i++) {
    if (items[i].item_type === TEMPLATE_ITEM_TYPE.IMAGE) {
      if (!organization.logo && items[i].image_src === "{{org.image}}") {
        items.splice(i, 1);
      }
      const imageLogo = items[i].image_src.replace("{{org.image}}", organization.logo);
      items[i].image_src = imageLogo;
    }
  }

  const imageIndex = items.findIndex((message) => message.item_type === TEMPLATE_ITEM_TYPE.IMAGE);
  if (imageIndex === -1) {
    return;
  }
  if (!organization.logo && items[imageIndex].image_src === "{{org.image}}") {
    items.splice(imageIndex, 1);
  }
  const imageLogo = items[imageIndex].image_src.replace("{{org.image}}", organization.logo);
  items[imageIndex].image_src = imageLogo;
}

function replaceInviteText(items, organization) {
  for (let i = 0; i < items.length; i++) {
    if (items[i].item_type === TEMPLATE_ITEM_TYPE.START_SURVEY) {
      if (items[i] && !items[i].text_button) {
        items[i].text_button = i18n.t("distribution.new.message.answer_now");
      }
    }

    if (items[i].item_type === TEMPLATE_ITEM_TYPE.SIGNATURE) {
      const signatureString = JSON.stringify(items[i].text).replaceAll("{{profile.name}}", organization.name);
      items[i].text = JSON.parse(signatureString);
    }
  }
}
